import React, { useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { BsPlayCircleFill } from 'react-icons/bs';

import BayModal from '../../BayModal';
import BayTooltip from '../../BayTooltip';
import BayWidgetLoadingPlaceholder from '../../BayWidgetLoadingPlaceholder';

interface PreviewFileModalProps {
	showPreviewFileModal: boolean;
	setShowPreviewFileModal: (show: boolean) => void;
	previewUrl: string;
	thumbnailUrl?: string;
	title: string;
	isLoading?: boolean;
	mimetype?: string;
}

const PreviewFileModal: React.FC<PreviewFileModalProps> = ({
	showPreviewFileModal,
	setShowPreviewFileModal,
	previewUrl,
	thumbnailUrl,
	title,
	isLoading = false,
	mimetype,
}) => {
	const [videoPlaying, setVideoPlaying] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);

	const onCancel = () => {
		setShowPreviewFileModal(false);
		setVideoPlaying(false);
	};

	const modalTitle = (
		<BayTooltip mouseEnterDelay={0.5} overlay={title} placement="bottom">
			<div className="text-truncate file-preview-title">{title}</div>
		</BayTooltip>
	);

	// Helper function to check if file is a video
	const isVideo = () => {
		if (!mimetype) {
			// Try to determine from the file extension if mimetype is not available
			const fileExtension = previewUrl.split('.').pop()?.toLowerCase();
			const videoExtensions = ['mp4', 'mov'];
			return fileExtension ? videoExtensions.includes(fileExtension) : false;
		}
		return mimetype.startsWith('video/');
	};

	// Handle play button click
	const handlePlayClick = () => {
		if (videoRef.current) {
			videoRef.current.play();
			setVideoPlaying(true);
		}
	};

	// Render media content based on type
	const renderMediaContent = () => {
		if (isLoading) {
			return <BayWidgetLoadingPlaceholder className="file-preview-loader" />;
		}

		if (isVideo()) {
			return (
				<Container className="d-flex align-items-center justify-content-center">
					<div
						className="video-container"
						style={{
							width: '100%',
							maxWidth: '720px',
							margin: '0 auto',
							position: 'relative',
							paddingBottom: '56.25%' /* 16:9 aspect ratio */,
							height: 0,
							overflow: 'hidden',
						}}
					>
						{!videoPlaying && (
							<div
								className="play-button-overlay"
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									zIndex: 10,
									cursor: 'pointer',
									backgroundColor: 'rgba(0, 0, 0, 0.1)',
								}}
								onClick={handlePlayClick}
							>
								<BsPlayCircleFill
									color="white"
									size={80}
									style={{
										filter: 'drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.5))',
										opacity: 0.9,
									}}
								/>
							</div>
						)}
						<video
							ref={videoRef}
							autoPlay={true}
							controls={videoPlaying}
							crossOrigin="anonymous"
							poster={thumbnailUrl}
							src={previewUrl}
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								objectFit: 'contain',
							}}
							onEnded={() => setVideoPlaying(false)}
							onPause={() => {
								// Only reset play button if video is at the beginning
								if (videoRef.current && videoRef.current.currentTime === 0) {
									setVideoPlaying(false);
								}
							}}
							onPlay={() => setVideoPlaying(true)}
						>
							<source src={previewUrl} type={mimetype} />
						</video>
					</div>
				</Container>
			);
		}

		// Default image display
		return (
			<Container
				className="d-flex align-items-center justify-content-center mb-3"
				style={{ minHeight: 320, maxHeight: 540 }}
			>
				<div
					className="d-flex align-items-center justify-content-center"
					style={{ position: 'relative', width: '80%', height: 'auto' }}
				>
					<img
						alt={`${title} Preview`}
						className="img-fluid"
						crossOrigin="anonymous"
						src={previewUrl}
						style={{ objectFit: 'contain', height: '100%', maxHeight: 540 }}
					/>
				</div>
			</Container>
		);
	};

	return (
		<BayModal
			buttonText="Close"
			className="fs-preview-modal"
			closeButton={true}
			height="80vh"
			hideCancel={true}
			id="file_preview_modal"
			show={showPreviewFileModal}
			size="lg"
			submitFunc={onCancel}
			title={modalTitle}
			onHideFunc={onCancel}
		>
			{renderMediaContent()}
		</BayModal>
	);
};

export default PreviewFileModal;
